import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  CircleCheck,
  CircleSlash,
  FilePenLine,
  PackageMinus,
  PackageOpen,
  PackagePlus,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
  Undo2,
} from 'lucide-react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import {
  abortWorkOrder,
  approveWorkOrder,
  closeWorkOrder,
  deleteWorkOrder,
  rejectWorkOrder,
  reopenWorkOrder,
  submitWorkOrder,
} from '#/api/momMgmt/workOrder';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '#/modules/commonComponents/dropdown-menu';
import {
  pickAvailableWorkflowExposedIds,
  resolveLgDesktopLayout,
  resolveMdDesktopLayout,
  resolveMobileLayout,
} from '#/modules/imsMgmt/receipts/shared/documentCommandBarLayout';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import { canCreateWorkOrderAmendment } from '#/modules/momMgmt/workOrder/helper/amendmentStatus';
import {
  getWorkOrderApiErrorMessage,
  isWorkOrderVersionConflict,
} from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  canAbortWorkOrder,
  canApproveWorkOrder,
  canCloseWorkOrder,
  canDeleteWorkOrderDraft,
  canEditWorkOrder,
  canEditWorkOrderRemark,
  canOperateWorkOrderStockDocs,
  canRejectWorkOrder,
  canReopenWorkOrder,
  canSubmitWorkOrder,
} from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { toast } from '#/utils/toast';
import {
  workOrderDetailQueryKey,
  workOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';

/**
 * 外露優先順序（依各狀態最常用操作）：
 * 草稿 提交／編輯；待核准 核准／退回；生產中 出庫／入庫；已結案 重開。
 * 結案、中止、刪除草稿等較少用或不可逆的操作收進「更多」。
 */
const WORKFLOW_EXPOSED_ORDER = [
  'submit',
  'approve',
  'reject',
  'outbound',
  'inbound',
  'edit',
  'reopen',
];

const ACTION_GROUPS = [
  { key: 'content', label: '單據內容', ids: ['edit', 'editRemark', 'createAmendment'] },
  { key: 'stock', label: '庫存操作', ids: ['outbound', 'inbound', 'transfer', 'return'] },
  {
    key: 'status',
    label: '單據狀態',
    ids: [
      'submit',
      'approve',
      'reject',
      'close',
      'abort',
      'reopen',
      'deleteDraft',
    ],
  },
];

const ACTION_SHAPE = PropTypes.shape({
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType,
  to: PropTypes.string,
  onSelect: PropTypes.func,
  destructive: PropTypes.bool,
  disabled: PropTypes.bool,
  available: PropTypes.bool,
  showExposedIcon: PropTypes.bool,
  exposedVariant: PropTypes.string,
  exposedClassName: PropTypes.string,
});

function getExposedActionIds(actionMap) {
  return pickAvailableWorkflowExposedIds(actionMap, WORKFLOW_EXPOSED_ORDER);
}

function runAfterMenuClose(callback) {
  window.setTimeout(() => {
    callback?.();
  }, 0);
}

function WorkOrderExposedActionButton({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);

  if (action.to && !disabled) {
    return (
      <Button
        asChild
        variant={action.exposedVariant || 'default'}
        className={cn(action.exposedClassName)}
      >
        <Link to={action.to} aria-label={action.label}>
          {action.showExposedIcon && Icon ? (
            <Icon className="size-3.5" aria-hidden />
          ) : null}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant={action.destructive ? 'destructive' : (action.exposedVariant || 'default')}
      className={cn(
        action.destructive && DESTRUCTIVE_SOLID_BUTTON_CLASS,
        action.exposedClassName,
      )}
      disabled={disabled}
      onClick={() => action.onSelect?.()}
    >
      {action.showExposedIcon && Icon ? (
        <Icon className="size-3.5" aria-hidden />
      ) : null}
      {action.label}
    </Button>
  );
}

WorkOrderExposedActionButton.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function WorkOrderMenuItem({ action, isPending }) {
  const Icon = action.icon;
  const disabled = Boolean(isPending || action.disabled);
  const iconNode = Icon ? <Icon className="size-4" aria-hidden /> : null;

  if (action.to && !disabled) {
    return (
      <DropdownMenuItem
        asChild
        variant={action.destructive ? 'destructive' : 'default'}
      >
        <Link to={action.to}>
          {iconNode}
          {action.label}
        </Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      variant={action.destructive ? 'destructive' : 'default'}
      disabled={disabled}
      onSelect={() => {
        if (disabled) {
          return;
        }
        runAfterMenuClose(action.onSelect);
      }}
    >
      {iconNode}
      {action.label}
    </DropdownMenuItem>
  );
}

WorkOrderMenuItem.propTypes = {
  action: ACTION_SHAPE.isRequired,
  isPending: PropTypes.bool,
};

function WorkOrderActionsMenu({
  triggerLabel,
  triggerAriaLabel,
  groups,
  isPending,
}) {
  if (!groups.length) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={isPending}
          aria-label={triggerAriaLabel || triggerLabel}
        >
          {triggerLabel}
          <ChevronDown className="size-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={cn('w-auto min-w-44')}>
        {groups.map((group, index) => (
          <Fragment key={group.key}>
            {index > 0 ? <DropdownMenuSeparator /> : null}
            <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
            {group.items.map((action) => (
              <WorkOrderMenuItem
                key={action.id}
                action={action}
                isPending={isPending}
              />
            ))}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

WorkOrderActionsMenu.propTypes = {
  triggerLabel: PropTypes.string.isRequired,
  triggerAriaLabel: PropTypes.string,
  groups: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(ACTION_SHAPE).isRequired,
  })).isRequired,
  isPending: PropTypes.bool,
};

function buildWorkOrderStockOpsCreatePath(route, workOrderId) {
  if (!workOrderId) {
    return route;
  }
  return `${route}?workOrderId=${encodeURIComponent(workOrderId)}`;
}

const WorkOrderCommandBar = ({ detail, onRefetch }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const canCreate = useCan(PERM.WORK_ORDER_CREATE);
  const canUpdate = useCan(PERM.WORK_ORDER_UPDATE);
  const canReview = useCan(PERM.WORK_ORDER_REVIEW);
  const canCreateAmendment = useCan(PERM.WORK_ORDER_AMENDMENT_CREATE);
  const canCreateTransfer = useCan(PERM.STOCK_TRANSFER_CREATE);
  const canCreateReturn = useCan(PERM.STOCK_RETURN_CREATE);

  const id = detail?.id;
  const updatedAt = detail?.record?.updatedAt ?? detail?.updatedAt;
  const status = detail?.status;

  async function invalidateAll() {
    await queryClient.invalidateQueries({ queryKey: workOrderListQueryKeyPrefix() });
    if (id) {
      await queryClient.invalidateQueries({ queryKey: workOrderDetailQueryKey(id) });
    }
    await onRefetch?.();
  }

  const commandMutation = useMutation({
    mutationFn: async ({ action, payload }) => {
      if (action === 'submit') {
        return submitWorkOrder(id, payload);
      }
      if (action === 'approve') {
        return approveWorkOrder(id, payload);
      }
      if (action === 'reject') {
        return rejectWorkOrder(id, payload);
      }
      if (action === 'close') {
        return closeWorkOrder(id, payload);
      }
      if (action === 'abort') {
        return abortWorkOrder(id, payload);
      }
      if (action === 'reopen') {
        return reopenWorkOrder(id, payload);
      }
      if (action === 'delete') {
        return deleteWorkOrder(id);
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectDialogOpen(false);
      }
      if (variables.action === 'delete') {
        await queryClient.invalidateQueries({ queryKey: workOrderListQueryKeyPrefix() });
        navigate(ROUTES.workOrder);
        return;
      }
      await invalidateAll();
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(error));
      if (isWorkOrderVersionConflict(error)) {
        onRefetch?.();
      }
    },
  });

  const isPending = commandMutation.isPending;

  if (!id) {
    return null;
  }

  async function handleSubmit() {
    const ok = await confirm({
      title: '提交工單',
      message: '提交後將進入待核准，確定提交？',
      confirmText: '提交',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'submit', payload: { updatedAt } });
  }

  async function handleApprove() {
    const ok = await confirm({
      title: '核准工單',
      message: '核准後將進入生產中，可進行入庫／出庫，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'approve', payload: { updatedAt } });
  }

  function handleRejectConfirm(rejectReason) {
    commandMutation.mutate({
      action: 'reject',
      payload: { updatedAt, rejectReason },
    });
  }

  async function handleClose() {
    const ok = await confirm({
      title: '結案工單',
      message: '結案前請確認出庫／入庫條件已滿足，確定結案？',
      confirmText: '結案',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'close', payload: { updatedAt } });
  }

  async function handleAbort() {
    const ok = await confirm({
      title: '中止工單',
      message: '中止後為終態（僅可改備註），確定中止？',
      confirmText: '中止',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'abort', payload: { updatedAt } });
  }

  async function handleReopen() {
    const ok = await confirm({
      title: '重開工單',
      message: '重開後將回到生產中，確定重開？',
      confirmText: '重開',
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'reopen', payload: { updatedAt } });
  }

  async function handleDeleteDraft() {
    const ok = await confirm({
      title: '刪除草稿',
      message: '刪除後無法復原，確定刪除？',
      confirmText: '刪除',
      destructive: true,
    });
    if (!ok) {
      return;
    }
    commandMutation.mutate({ action: 'delete' });
  }

  const actionMap = {
    outbound: {
      id: 'outbound',
      label: '出庫',
      icon: PackageMinus,
      to: ROUTES.workOrderOutboundCreate(id),
      available: canCreate && canOperateWorkOrderStockDocs(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    inbound: {
      id: 'inbound',
      label: '入庫',
      icon: PackagePlus,
      to: ROUTES.workOrderInboundCreate(id),
      available: canCreate && canOperateWorkOrderStockDocs(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    transfer: {
      id: 'transfer',
      label: '轉倉',
      icon: ArrowLeftRight,
      to: buildWorkOrderStockOpsCreatePath(ROUTES.workOrderTransferCreate, id),
      available: canCreateTransfer && canOperateWorkOrderStockDocs(status),
    },
    return: {
      id: 'return',
      label: '退料',
      icon: PackageOpen,
      to: buildWorkOrderStockOpsCreatePath(ROUTES.workOrderReturnCreate, id),
      available: canCreateReturn && canOperateWorkOrderStockDocs(status),
    },
    createAmendment: {
      id: 'createAmendment',
      label: '新建異動單',
      icon: FilePenLine,
      to: ROUTES.workOrderAmendmentCreate(id),
      available: canCreateAmendment && canCreateWorkOrderAmendment(detail),
    },
    edit: {
      id: 'edit',
      label: '編輯',
      icon: Pencil,
      to: ROUTES.workOrderEdit(id),
      available: canUpdate && canEditWorkOrder(detail),
      showExposedIcon: true,
      exposedVariant: 'default',
      exposedClassName: 'document-detail-edit-action',
    },
    editRemark: {
      id: 'editRemark',
      label: '編輯備註',
      icon: FilePenLine,
      to: ROUTES.workOrderRemark(id),
      available: canUpdate && canEditWorkOrderRemark(status),
      showExposedIcon: true,
      exposedVariant: 'outline',
    },
    submit: {
      id: 'submit',
      label: '提交',
      icon: Send,
      available: canCreate && canSubmitWorkOrder(status),
      onSelect: handleSubmit,
    },
    approve: {
      id: 'approve',
      label: '核准',
      icon: Check,
      available: canReview && canApproveWorkOrder(status),
      onSelect: handleApprove,
    },
    reject: {
      id: 'reject',
      label: '退回',
      icon: Undo2,
      available: canReview && canRejectWorkOrder(status),
      destructive: true,
      onSelect: () => setRejectDialogOpen(true),
    },
    close: {
      id: 'close',
      label: '結案',
      icon: CircleCheck,
      available: canReview && canCloseWorkOrder(status),
      onSelect: handleClose,
    },
    abort: {
      id: 'abort',
      label: '中止',
      icon: CircleSlash,
      available: canReview && canAbortWorkOrder(status),
      destructive: true,
      onSelect: handleAbort,
    },
    reopen: {
      id: 'reopen',
      label: '重開',
      icon: RotateCcw,
      available: canReview && canReopenWorkOrder(status),
      onSelect: handleReopen,
    },
    deleteDraft: {
      id: 'deleteDraft',
      label: '刪除草稿',
      icon: Trash2,
      available: canCreate && canDeleteWorkOrderDraft(status),
      destructive: true,
      onSelect: handleDeleteDraft,
    },
  };

  const mobileLayout = resolveMobileLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
  });
  const mdLayout = resolveMdDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });
  const lgLayout = resolveLgDesktopLayout({
    actionMap,
    actionGroups: ACTION_GROUPS,
    getExposedActionIds,
  });

  function renderLayoutRow(
    layout,
    { triggerLabel = '更多', triggerAriaLabel = '更多操作' } = {},
  ) {
    return (
      <>
        {layout.flatActions.map((action) => (
          <WorkOrderExposedActionButton
            key={action.id}
            action={action}
            isPending={isPending}
          />
        ))}
        {layout.showMoreMenu ? (
          <WorkOrderActionsMenu
            triggerLabel={triggerLabel}
            triggerAriaLabel={triggerAriaLabel}
            groups={layout.menuGroups}
            isPending={isPending}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectDialogOpen}
        onOpenChange={setRejectDialogOpen}
        title="退回工單"
        description="退回後將恢復為草稿，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />

      <div className={cn('flex items-center justify-end gap-2')}>
        <div className={cn('flex md:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mobileLayout, {
            triggerLabel: '操作',
            triggerAriaLabel: '單據操作',
          })}
        </div>
        <div className={cn('hidden md:flex lg:hidden', 'items-center gap-2')}>
          {renderLayoutRow(mdLayout)}
        </div>
        <div className={cn('hidden lg:flex', 'items-center gap-2')}>
          {renderLayoutRow(lgLayout)}
        </div>
      </div>
    </>
  );
};

WorkOrderCommandBar.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    status: PropTypes.string,
    amendments: PropTypes.arrayOf(PropTypes.object),
    remark: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      updatedAt: PropTypes.string,
    }),
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default WorkOrderCommandBar;
